/**
 * Which single state an API key is in, and the wording to show for it.
 *
 * A key record carries three independent facts — revokedAt, expiresAt and
 * lastUsedAt — and they are NOT mutually exclusive: a key can be revoked
 * after it already expired, or revoked before it was ever used. Rendering
 * every true fact at once ("revoked · expired · never used") reads as noise
 * on a list a bookkeeper has to scan quickly, so this picks ONE state, in a
 * fixed priority, so the same key always reads the same way wherever it is
 * shown:
 *
 *   revoked > expired > never used > active
 *
 * Revoked outranks expired because it is an administrator's own act, not a
 * clock running out — it is the more important fact about the key, and the
 * one most likely to be the answer to "why did this integration stop
 * working". Expired outranks never-used because "this credential cannot
 * authenticate at all" matters more than "nobody has used it yet".
 *
 * Never-used is a state in its own right, not a footnote on a "last used"
 * column: a dormant credential is either a broken integration nobody has
 * noticed, or one nobody needs — and both are worth an administrator seeing
 * without having to read a date column and do the "never means never" math
 * themselves.
 */
export function apiKeyState(key, now = new Date()) {
  const revoked = Boolean(key?.revokedAt);
  // A null expiresAt means the key never expires, not that it expired at
  // time zero — so the expiry check only fires when a real date is present.
  const expired = Boolean(key?.expiresAt) && new Date(key.expiresAt).getTime() <= now.getTime();
  const neverUsed = !key?.lastUsedAt;

  if (revoked) return { state: 'revoked', label: 'Revoked' };
  if (expired) return { state: 'expired', label: 'Expired' };
  if (neverUsed) return { state: 'never-used', label: 'Never used' };
  return { state: 'active', label: 'Active' };
}
